import { Component, ErrorInfo, ReactNode, StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { App } from "./App";
import { DebugBadge } from "./debug/DebugBadge";
import { DialogProvider } from "./components/Dialog";
import { getToken, ladeMich } from "./api";
import { installErrorLogging, reportClientError } from "./lib/errorlog";
// Side-effect import — applies the persisted theme and mirrors it into
// the parent-domain cookie *before* React mounts. Without this, the
// first paint can flash the default palette and the `sites.*` login
// page won't see the cookie until after the user opens a page that
// happens to reach a ThemePicker / EmailBody.
import "./lib/theme";
import "./index.css";
// Setzt Schrift und Phasenstärke, bevor das erste Bild steht.
import "./lib/darstellung";
// Die Bausteinsammlung (`src/ui`) — eine Datei, aus der jede Fläche, jeder
// Knopf und jede Zeile ihr Aussehen bezieht.
import "./ui/ui.css";
import "./features/lights/lights.css";
import "./components/datepicker.css";
import { registerServiceWorker } from "./lib/push";

// Best-effort SW registration on every load so the manifest install prompt
// can appear and push delivery keeps working after browser restarts. The
// actual push subscription is only created when the user opts in from the
// Security page — see features/security/NotificationsCard.tsx.
void registerServiceWorker();

// Capture uncaught errors + unhandled rejections and forward them to the
// server log so they surface on the /logs page.
installErrorLogging();

// Catch render-time errors anywhere in the tree, report them, and show a
// recoverable fallback instead of a blank screen.
class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    reportClientError(
      error.message || "Render error",
      location.pathname,
      `${error.stack ?? ""}\n${info.componentStack ?? ""}`,
    );
  }
  render() {
    if (this.state.error) {
      return (
        <div className="page">
          <h1>Something went wrong</h1>
          <p className="muted">
            The error was logged (Logs page). Reload the page.
          </p>
          <button className="btn-primary" onClick={() => location.reload()}>Reload</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Wer ist angemeldet, und was darf er? Das entscheidet, was in der Leiste
// steht. Der gespeicherte Stand trägt den ersten Bildaufbau, die Antwort
// vom Server korrigiert ihn gleich darauf.
if (getToken()) void ladeMich();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <DialogProvider>
          <App />
          <DebugBadge />
        </DialogProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </StrictMode>,
);
